import { useQuery } from "@apollo/client";
import { Breadcrumb, Button } from "pepsico-ds";
import { useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SummaryField from "../../components/customizedRewards/SummaryField";
import { GlobalConfigContext } from "../../context/GlobalConfigContext";
import { listCustomizedEarningsById } from "../../graphql/queries/listCustomizedEarningsById";
import { paths } from "../../routes/paths";
import { formatDateTz } from "../../utils/commons";
import { getUniqueCampaignIds } from "../../utils/rewardMappingUtils";
import "./customizedRewardDetails.scss";
import listCampaigns from "./queries/listCampaigns";
import listRewards from "./queries/listRewards";

const CustomizedRewardDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { programTimezone } = useContext(GlobalConfigContext);

  const { data: rewardQueryData, loading: queryLoading } = useQuery(
    listCustomizedEarningsById,
    {
      variables: { id },
    }
  );

  const rewardData = rewardQueryData?.listCustomizedEarningsById;

  const { data: campaignsData, loading: campaignsLoading } = useQuery(
    listCampaigns,
    {
      fetchPolicy: "cache-first", // Use cache if available
      skip: !rewardData?.rewardMappings?.length,
    }
  );

  const { data: rewardsData } = useQuery(listRewards, {
    variables: {
      filter: {},
      page: 0,
      size: 1000,
    },
    skip: !rewardData?.rewardMappings?.length,
    fetchPolicy: "cache-first", // Use cache if available
  });

  const handleCancel = () => {
    navigate(`/${paths.rewards}/${paths.customized_rewards}`);
  };

  const handleEdit = () => {
    navigate(`/${paths.rewards}/${paths.customized_rewards}/${paths.edit}/${id}`);
  };

  if (queryLoading || campaignsLoading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading reward details...</p>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="wrap">
        <Breadcrumb
          breadcrumbs={[
            {
              label: "Rewards",
              href: `/${paths.rewards}`,
            },
            {
              label: "Customized Rewards",
              href: `/${paths.rewards}/${paths.customized_rewards}`,
            },
            {
              label: rewardData?.name || "Reward Details",
            },
          ]}
          activeIndex={2}
        />

        <div className="customized-reward-details-container">
          <div className="card">
            <h3>Basic Setup</h3>
            <div className="flex">
              <SummaryField data={rewardData?.name} label="Reward Name" />
              <SummaryField data={rewardData?.newQuantity} label="Quantity" />
              <SummaryField
                data={formatDateTz(
                  rewardData?.startDate,
                  programTimezone,
                  "DD/MM/YYYY"
                )}
                label="Start Date"
              />
              <SummaryField
                data={formatDateTz(
                  rewardData?.endDate,
                  programTimezone,
                  "DD/MM/YYYY"
                )}
                label="End Date"
              />
            </div>
          </div>

          <div className="card">
            <h3>Campaigns Linked</h3>
            <SummaryField
              data={
                getUniqueCampaignIds(rewardData?.rewardMappings)
                  ?.map(
                    (campaignId) =>
                      campaignsData?.listCampaigns.find(
                        (campaign) => campaign.id === campaignId
                      )?.title || campaignId
                  )
                  .join(", ") || "-"
              }
            />
          </div>

          <div className="card">
            <h3>Rewards</h3>
            <SummaryField
              data={
                rewardData?.rewardMappings
                  ?.map((mapping) => {
                    const reward = rewardsData?.listRewards?.items?.find(
                      (r) => r.id === mapping.rewardId
                    );
                    return reward?.name || mapping.rewardId;
                  })
                  .join(", ") || "-"
              }
            />
          </div>
        </div>

        <div className="action-buttons">
          <Button variant="secondary" size="medium" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="medium"
            onClick={handleEdit}
            iconTrailing="arrow_forward"
          >
            Edit
          </Button>
        </div>
      </div>
    </div>
  );
};

// No props needed as we're using useParams to get the ID

export default CustomizedRewardDetailsPage;
